import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuthDataState } from '@/routes/shared/_authDataState'
import { useFeatureFlags } from '@/rules/get-feature-flags'
import { api } from '@/routes/shared/_api'
import { useToast } from '@/hooks/use-toast'
import { Loader } from '@/components/Loader'
import Login from '@/routes/shared/Login'

export const WatchlistAdd = () => {
  const { drupalNodeId } = useParams<{ drupalNodeId: string }>()
  const { isLoading: userIsLoading, data: authData } = useAuthDataState()
  const { data: featureFlagsData, isLoading: flagsLoading } = useFeatureFlags()
  const nav = useNavigate()
  const { toast } = useToast()
  const [error, setError] = useState<string | null>(null)
  const attempted = useRef(false)

  useEffect(() => {
    if (!authData || flagsLoading || attempted.current) return
    attempted.current = true

    if (!featureFlagsData?.ff_watchlist) {
      nav('/dashboard/investments/active', { replace: true })
      return
    }

    const addToWatchlist = async () => {
      try {
        // Resolve drupal node ID to io_uuid
        const resolveResp = await api.get(
          `/dashboard/investments/resolve-io/${drupalNodeId}`
        )
        const ioUuid = resolveResp.io_uuid

        // Add to watchlist
        await api.post(`/dashboard/investments/${ioUuid}/watchlist`)

        toast({
          variant: 'success',
          description: 'Investment added to your watchlist.',
          position: 'top',
        })
        nav('/dashboard/investments/watchlist', { replace: true })
      } catch (err: any) {
        const status = err?.response?.status
        if (status === 409) {
          // Already watchlisted or terminal IO — treat as success
          toast({
            variant: 'success',
            description: 'Investment is already on your watchlist.',
            position: 'top',
          })
          nav('/dashboard/investments/watchlist', { replace: true })
        } else {
          setError('Unable to add this investment to your watchlist.')
        }
      }
    }

    addToWatchlist()
  }, [authData, featureFlagsData, flagsLoading, drupalNodeId, nav, toast])

  // Auth still loading
  if (userIsLoading) {
    return (
      <div className="flex items-center justify-center h-[75vh]">
        <Loader />
      </div>
    )
  }

  // Not authenticated — show login (check before flagsLoading since flags depend on auth)
  if (!authData) return <Login />

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center p-8">
          <p className="text-lg text-content-black mb-4">{error}</p>
          <button
            className="text-primary underline"
            onClick={() =>
              nav('/dashboard/investments/active', { replace: true })
            }
          >
            Go to your investments
          </button>
        </div>
      </div>
    )
  }

  // Authenticated — waiting for flags or watchlist add in progress
  return (
    <div className="flex items-center justify-center h-[75vh]">
      <Loader />
    </div>
  )
}
